import { useEffect, useState } from 'react';
import type { PutRoutingSkuTargetRequest, RoutingBreakerIdentity } from '@nadoutong/contracts';
import { useTripRoutingBreaker } from '../api/useRoutingBreakers.ts';
import {
  useSetRoutingConfigSkuTarget,
  useSetRoutingDefaultSkuTarget,
} from '../api/useTenantRouting.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, ConfirmDialog, Dialog, FormField, Select } from '../ui/index.ts';
import {
  TRIP_DURATION_MS,
  TRIP_DURATION_OPTIONS,
  tripDurationLabelKey,
} from './tenantRoutingShared.ts';
import type { TripDuration } from './tenantRoutingShared.ts';

/**
 * Shared force-open (trip) dialog for the routing breaker control plane
 * (epic #886). Used by both the breakers drawer (existing entries) and
 * the config-sheet pool rows (healthy or organically-open targets), so
 * the duration presets, plain-language consequence copy, and confirm
 * flow cannot drift between the two entry points. Owns the trip
 * mutation — callers only provide the identity four-tuple and a display
 * label.
 */

export type PermanentTargetDisable =
  | {
    readonly layer: 'default';
    readonly skuId: string;
    readonly body: PutRoutingSkuTargetRequest;
  }
  | {
    readonly layer: 'config';
    readonly configId: string;
    readonly skuId: string;
    readonly body: PutRoutingSkuTargetRequest;
  };

export interface BreakerTripTarget {
  /** Identity four-tuple addressing the breaker entry to force open. */
  readonly identity: RoutingBreakerIdentity;
  /** Human-readable identity line shown in the dialog description. */
  readonly label: string;
  /**
   * Present on a tenant-routing target row. Choosing 永久 writes
   * `enabled: false` through the existing target PUT instead of force-open.
   */
  readonly permanentDisable?: PermanentTargetDisable;
}

type DialogDuration = TripDuration | 'permanent';

function isTripDuration(value: string): value is TripDuration {
  return TRIP_DURATION_OPTIONS.some((option) => option === value);
}

export function RoutingBreakerTripDialog({
  target,
  onClose,
}: {
  readonly target: BreakerTripTarget | null;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const tripBreaker = useTripRoutingBreaker();
  const setDefault = useSetRoutingDefaultSkuTarget();
  const setConfig = useSetRoutingConfigSkuTarget();
  const [duration, setDuration] = useState<DialogDuration>('1h');
  const [permanentOpen, setPermanentOpen] = useState(false);

  // Reset the preset whenever a new target opens the dialog.
  useEffect(() => {
    if (target !== null) {
      setDuration('1h');
      setPermanentOpen(false);
    }
  }, [target]);

  function handleConfirm(): void {
    if (target === null) return;
    if (duration === 'permanent') {
      setPermanentOpen(true);
      return;
    }
    const until = new Date(Date.now() + TRIP_DURATION_MS[duration]).toISOString();
    tripBreaker.mutate(
      {
        upstream_id: target.identity.upstream_id,
        account_id: target.identity.account_id,
        key_bucket_id: target.identity.key_bucket_id,
        upstream_channel_id: target.identity.upstream_channel_id,
        until,
      },
      {
        onSuccess: () => {
          toast.success(t('tenant_routing.breaker.toast.tripped'));
          onClose();
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  async function disablePermanently(): Promise<void> {
    const spec = target?.permanentDisable;
    if (spec === undefined) return;
    try {
      if (spec.layer === 'default') {
        await setDefault.mutateAsync({ skuId: spec.skuId, body: spec.body });
      } else {
        await setConfig.mutateAsync({ configId: spec.configId, skuId: spec.skuId, body: spec.body });
      }
      toast.success(t('tenant_routing.probe.disable_done'));
      setPermanentOpen(false);
      onClose();
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  const permanentPending = setDefault.isPending || setConfig.isPending;
  const durationOptions = [
    ...TRIP_DURATION_OPTIONS.map((option) => ({
      value: option,
      label: t(tripDurationLabelKey(option)),
    })),
    ...(target?.permanentDisable === undefined
      ? []
      : [{ value: 'permanent', label: t('tenant_routing.breaker.trip_duration.permanent') }]),
  ];

  return (
    <>
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('tenant_routing.breaker.trip_title')}
      description={target?.label}
      closeLabel={t('common.close')}
      testId="routing-breaker-trip-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={tripBreaker.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={tripBreaker.isPending}
            onClick={handleConfirm}
            data-testid="routing-breaker-trip-save"
          >
            {t('tenant_routing.breaker.trip_save')}
          </Button>
        </>
      }
    >
      {target === null ? null : (
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t('tenant_routing.breaker.trip_body')}
          </p>
          <FormField label={t('tenant_routing.breaker.trip_duration')} fieldId="routing-breaker-trip-duration">
            <Select
              value={duration}
              onValueChange={(value) => {
                if (value === 'permanent' && target?.permanentDisable !== undefined) {
                  setDuration('permanent');
                  return;
                }
                if (isTripDuration(value)) setDuration(value);
              }}
              options={durationOptions}
              testId="routing-breaker-trip-duration"
            />
          </FormField>
          <p className="m-0 text-dense text-paper-low" data-testid="routing-breaker-trip-preview">
            {duration === 'permanent'
              ? t('tenant_routing.breaker.permanent_body')
              : t('tenant_routing.breaker.trip_until_preview', {
                until: formatLocalTimestamp(
                  new Date(Date.now() + TRIP_DURATION_MS[duration]).toISOString(),
                ),
              })}
          </p>
        </div>
      )}
    </Dialog>
    <ConfirmDialog
      open={permanentOpen}
      onOpenChange={setPermanentOpen}
      tone="danger"
      title={t('tenant_routing.breaker.permanent_title')}
      body={t('tenant_routing.breaker.permanent_body')}
      confirmLabel={t('tenant_routing.breaker.permanent_confirm')}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      loading={permanentPending}
      testId="routing-breaker-permanent-dialog"
      onConfirm={() => { void disablePermanently(); }}
    />
    </>
  );
}
